/**
 * Mordeczka.eu Admin - Roles Page with Ant Design
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Typography,
  Popconfirm,
  message,
  Card,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { rolesApi } from "../services/api";
import type { Role } from "../types";
import { usePermissions } from "../hooks/usePermissions";

const { Title } = Typography;

export function RolesPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Role | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 100 });
  const { canEdit, canDelete } = usePermissions();

  const { data, isLoading } = useQuery({
    queryKey: ["roles", pagination],
    queryFn: () =>
      rolesApi.list({
        page: pagination.current,
        page_size: pagination.pageSize,
      }),
  });

  const createMutation = useMutation({
    mutationFn: rolesApi.create,
    onSuccess: () => {
      message.success("Rola utworzona pomyślnie");
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      closeModal();
    },
    onError: () => message.error("Nie udało się utworzyć"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string } }) =>
      rolesApi.update(id, data),
    onSuccess: () => {
      message.success("Rola zaktualizowana pomyślnie");
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      closeModal();
    },
    onError: () => message.error("Nie udało się zaktualizować"),
  });

  const deleteMutation = useMutation({
    mutationFn: rolesApi.delete,
    onSuccess: () => {
      message.success("Rola usunięta pomyślnie");
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
    onError: () => message.error("Nie udało się usunąć"),
  });

  const openModal = (item?: Role) => {
    setEditingItem(item || null);
    form.setFieldsValue({
      name: item?.name || "",
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingItem) {
        updateMutation.mutate({ id: editingItem.id, data: values });
      } else {
        createMutation.mutate(values);
      }
    } catch {
      // Validation failed
    }
  };

  const columns: ColumnsType<Role> = [
    {
      title: "Nazwa",
      dataIndex: "name",
      key: "name",
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
      sorter: (a: Role, b: Role) => a.name.localeCompare(b.name),
    },
    {
      title: "Akcje",
      key: "actions",
      width: 120,
      render: (_: unknown, record: Role) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openModal(record)}
          />
          <Popconfirm
            title="Usunąć tę rolę?"
            description="Tej operacji nie można cofnąć."
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText="Usuń"
            cancelText="Anuluj"
            okButtonProps={{ danger: true }}
            disabled={!canDelete}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={!canDelete}
              title={!canDelete ? "Brak uprawnień do usuwania" : "Usuń"}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          Role
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openModal()}
        >
          Dodaj nową
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={data?.items || []}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data?.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ["100", "500", "1000"],
            showTotal: (total: number) => `Łącznie ${total} ról`,
            onChange: (page: number, pageSize: number) =>
              setPagination({ current: page, pageSize }),
          }}
        />
      </Card>

      <Modal
        title={editingItem ? "Edytuj rolę" : "Dodaj rolę"}
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={closeModal}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        okButtonProps={{
          disabled: !canEdit,
          title: !canEdit ? "Brak uprawnień do edycji" : undefined,
        }}
        okText="Zapisz"
        cancelText="Anuluj"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Nazwa"
            rules={[{ required: true, message: "Proszę wprowadzić nazwę" }]}
          >
            <Input placeholder="np. user, admin" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default RolesPage;
